import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";

// css
import styles from "./addManage.module.scss";
import { Link, useNavigate, useParams } from "react-router-dom";
import Switch from "react-switch";
import AppProfilePictureField from "components/shared/FormComponents/AppProfilePictureField";
import { Formik } from "formik";
import AppDatePicker from "components/shared/FormComponents/AppDatePicker";
import { CLIENT_TYPES, MIME_TYPES } from "config/constants";
import AppInputField from "components/shared/FormComponents/AppInputField";
import createGroupSchema from "./addGroup.schema";
import { MANAGE_GROUP } from "config/paths";
import { CommonSelect, RequiredFieldAstrick } from "components/Common/Common";
import AddNewMemberModal from "components/Modals/AddNewMember";
import { useAddGroupMutation, useGetGroupDetailsQuery, useUpdateGroupMutation } from "store/services/group";
import { errorMessageToast, successMessageToast } from "helpers/common";
import { cloneDeep, isEmpty } from "lodash";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { genericFormErrorMsg } from "utils/validation/validationHelpers";

const AddNewGroup = () => {

  const { id: groupId } = useParams();
  const editMode = !!groupId;

  const navigate = useNavigate();

  const { data: groupDetails, isLoading } = useGetGroupDetailsQuery(groupId, { skip: !groupId });

  const [initialValues, setInitialValues] = useState({ group_name: "", start_date: "", end_date: "", group_type: { label: "", value: "" }, description: "", group_image: "" })
  const [status, setStatus] = useState("active");
  const [showModal, setShowModal] = useState(false);
  const [members, setMembers] = useState([]);

  const [createGroup, { isLoading: createGroupLoading }] = useAddGroupMutation();
  const [updateGroup, { isLoading: updateGroupLoading }] = useUpdateGroupMutation();

  const handleChange = () => {
    setStatus(status == "active" ? "archived" : "active");
  };

  const handleSubmit = async (values) => {
    try {

      if (!members?.length) return errorMessageToast("Please select atleast one member");

      let payload = cloneDeep(values);
      payload.group_type = values?.group_type?.value;
      payload.members = members;
      payload.status = status;

      const response = editMode ? await updateGroup({ id: groupId, data: payload }) : await createGroup(payload);
      if (!response?.error) {
        successMessageToast(response?.data?.message);
        navigate(MANAGE_GROUP)
      }
    } catch (error) { console.log("Error creating group"); };
  };

  useEffect(() => {

    if (!groupDetails) return;

    setStatus(groupDetails?.status);
    setMembers(groupDetails?.members?.map(i => i?.user?.id));

    setInitialValues({
      group_name: groupDetails?.group_name || "",
      start_date: groupDetails?.start_date || "",
      end_date: groupDetails?.end_date || "",
      group_type: CLIENT_TYPES.find(x => x.value === groupDetails?.group_type) || { label: "", value: "" },
      description: groupDetails?.description || "",
      group_image: groupDetails?.group_image || ""
    })

  }, [groupDetails]);

  return (
    <>

      <section
        className={`${styles.addManage} position-relative py-4 addManage`}
      >
        <Container fluid>
          {isLoading ? <LoadingIndicator /> : <Row>
            <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
              <div className="left">
                <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10 flex-wrap">
                  <li className="">
                    <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit Group" : "Add New Groups"}</h6>
                  </li>
                </ul>
              </div>
              <div className="right">
                <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center justify-content-center flex-wrap gap-10">
                  <li className="">
                    <Link
                      className="btn text-white d-flex align-items-center justify-content-center commonBtn fw-sbold "
                      onClick={() => setShowModal(true)}
                    >
                      <span className="icn me-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="19"
                          height="19"
                          viewBox="0 0 19 19"
                          fill="none"
                        >
                          <mask
                            id="mask0_0_4440"
                            style={{ maskType: "alpha" }}
                            maskUnits="userSpaceOnUse"
                            x="0"
                            y="0"
                            width="19"
                            height="19"
                          >
                            <rect
                              width="18.7197"
                              height="18.7197"
                              fill="#D9D9D9"
                            />
                          </mask>
                          <g mask="url(#mask0_0_4440)">
                            <path
                              d="M8.57996 13.2594H10.1399V10.1394H13.2599V8.57947H10.1399V5.45951H8.57996V8.57947H5.46V10.1394H8.57996V13.2594ZM9.35995 17.1593C8.28096 17.1593 7.26698 16.9546 6.31799 16.5451C5.369 16.1356 4.54352 15.5799 3.84153 14.8779C3.13954 14.1759 2.58379 13.3504 2.1743 12.4014C1.76481 11.4524 1.56006 10.4384 1.56006 9.35946C1.56006 8.28047 1.76481 7.26649 2.1743 6.3175C2.58379 5.36851 3.13954 4.54303 3.84153 3.84104C4.54352 3.13905 5.369 2.58331 6.31799 2.17381C7.26698 1.76432 8.28096 1.55957 9.35995 1.55957C10.4389 1.55957 11.4529 1.76432 12.4019 2.17381C13.3509 2.58331 14.1764 3.13905 14.8784 3.84104C15.5804 4.54303 16.1361 5.36851 16.5456 6.3175C16.9551 7.26649 17.1598 8.28047 17.1598 9.35946C17.1598 10.4384 16.9551 11.4524 16.5456 12.4014C16.1361 13.3504 15.5804 14.1759 14.8784 14.8779C14.1764 15.5799 13.3509 16.1356 12.4019 16.5451C11.4529 16.9546 10.4389 17.1593 9.35995 17.1593ZM9.35995 15.5994C11.1019 15.5994 12.5774 14.9949 13.7864 13.7859C14.9954 12.5769 15.5999 11.1014 15.5999 9.35946C15.5999 7.61748 14.9954 6.142 13.7864 4.93302C12.5774 3.72404 11.1019 3.11955 9.35995 3.11955C7.61797 3.11955 6.14249 3.72404 4.93351 4.93302C3.72453 6.142 3.12004 7.61748 3.12004 9.35946C3.12004 11.1014 3.72453 12.5769 4.93351 13.7859C6.14249 14.9949 7.61797 15.5994 9.35995 15.5994Z"
                              fill="white"
                            />
                          </g>
                        </svg>
                      </span>{" "}
                      Add New Clients
                    </Link>
                  </li>
                  <li className="d-flex align-items-center gap-10 px-2">
                    <label
                      htmlFor=""
                      className="form-label themeBlue fw-bold mb-0"
                    >
                      Activate Status
                    </label>
                    <div className=" d-flex align-items-center">
                      <p className="m-0 text-muted mx-1">Inactive</p>
                      <Switch
                        uncheckedIcon={""}
                        checkedIcon={""}
                        onHandleColor={"#A3DF00"}
                        offHandleColor={"#A3DF00"}
                        onColor={"#E6E6E6"}
                        offColor={"#E6E6E6"}
                        height={18}
                        width={40}
                        onChange={handleChange}
                        checked={status == "active"}
                      />
                      <p className="m-0 text-muted mx-1">Active</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <Col lg="12" className="my-2">
              <Formik
                initialValues={initialValues}
                validationSchema={createGroupSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {
                  ({
                    handleSubmit,
                    isSubmitting,
                    values,
                    errors,
                    setFieldValue,
                    submitCount
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <AddNewMemberModal
                        show={showModal}
                        setShow={setShowModal}
                        clientType={values?.group_type?.value}
                        setMembers={setMembers}
                      />
                      <div
                        className="py-3"
                        style={{ borderTop: "1px solid #41a2a84d" }}
                      >
                        <Row>
                          <Col lg="3" md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0"
                            >
                              Group Image
                              <RequiredFieldAstrick />
                            </label>
                            <AppProfilePictureField
                              name={"group_image"}
                              acl={"public-read"}
                              allowedTypes={[MIME_TYPES.jpeg, MIME_TYPES.jpg, MIME_TYPES.png]}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0"
                            >
                              Group Name
                              <RequiredFieldAstrick />
                            </label>
                            <AppInputField
                              name="group_name"
                              type="text"
                              placeholder="e.g John"
                              className={`${styles.formControl} form-control`}
                              maxLength={100}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0"
                            >
                              Start Date
                              <RequiredFieldAstrick />
                            </label>
                            <AppDatePicker
                              portalId={"root"}
                              name="start_date"
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>

                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0"
                            >
                              End Date
                              <RequiredFieldAstrick />
                            </label>
                            <AppDatePicker
                              portalId={"root"}
                              name="end_date"
                              className={`${styles.formControl} form-control`}
                            />
                          </Col>
                          <Col md="4" sm="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0"
                            >
                              Group Type
                              <RequiredFieldAstrick />
                            </label>
                            <CommonSelect
                              name="group_type"
                              placeholder={"Select Group Type"}
                              option={CLIENT_TYPES}
                              isDisabled={editMode}
                            />
                          </Col>
                          <Col md="12" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label themeBlue fw-bold mb-0"
                            >
                              About
                              <span className="text-muted mx-1 text-sm">
                                (Description)
                              </span>
                            </label>
                            <AppInputField
                              name="description"
                              fieldType="textarea"
                              placeholder=""
                              className={`${styles.formControl} ${styles.textarea} form-control`}
                              rows={5}
                              maxLength={500}
                            />
                          </Col>
                        </Row>
                      </div>

                      <Row className="mt-5 ">
                        <Col className="my-2" lg="12">
                          {(!isEmpty(errors) && submitCount > 0) && (
                            <Row className="my-2">
                              <Col lg="12">
                                <span className="error">{genericFormErrorMsg}</span>
                              </Col>
                            </Row>
                          )}
                          <div
                            className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                            style={{ borderTop: "1px solid #41a2a84d" }}
                          >
                            <Button
                              className="d-flex align-items-center justify-content-center commonBtn"
                              type="submit"
                              disabled={createGroupLoading || updateGroupLoading}
                            >
                              {createGroupLoading || updateGroupLoading ? <LoadingIndicator /> : "Save"}
                            </Button>
                            <Button
                              className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                              type="button"
                              onClick={() => navigate(MANAGE_GROUP)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}

              </Formik>
            </Col>
          </Row>}
        </Container>
      </section>
    </>
  );
};

export default AddNewGroup;
